import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import authApi from '../api/authApi';
import { AuthUser, AuthTokens, LoadingStatus } from '../../types';
import { getDeviceToken } from '../../utils/deviceToken';
import { disablePushNotifications } from './notificationsSlice';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  status: LoadingStatus;
  initialized: boolean;
  error: string | null;
  accountStatus: string | null;
  suspensionReason: string | null;
  pendingUserId: string | null;
  pendingAction: string | null;
  pendingMaskedPhone: string | null;
  pendingChannel: 'sms' | 'email' | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  status: 'idle',
  // Auth tokens live in an httpOnly cookie the client can't read, so route guards
  // wait for the bootstrap fetchMe to settle before deciding where to send the user.
  initialized: false,
  error: null,
  accountStatus: null,
  suspensionReason: null,
  pendingUserId: null,
  pendingAction: null,
  pendingMaskedPhone: null,
  pendingChannel: null,
};

export const registerUser = createAsyncThunk(
  'auth/register',
  async (payload: { email: string; phone: string; password: string }, { rejectWithValue }) => {
    try {
      return await authApi.register(payload);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Registration failed');
    }
  },
);

export const loginUser = createAsyncThunk(
  'auth/login',
  async (payload: { email: string; password: string }, { rejectWithValue }) => {
    try {
      return await authApi.login({ ...payload, deviceToken: getDeviceToken() });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string; data?: { accountStatus?: string; suspensionReason?: string } } } };
      const errorData = e.response?.data;
      return rejectWithValue({
        message: errorData?.message || 'Login failed',
        accountStatus: errorData?.data?.accountStatus ?? null,
        suspensionReason: errorData?.data?.suspensionReason ?? null,
      });
    }
  },
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async (payload: { userId: string; otp: string; action: string }, { rejectWithValue }) => {
    try {
      const res = await authApi.verifyOtp({ ...payload, deviceToken: getDeviceToken() });
      return res.data as AuthTokens;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'OTP verification failed');
    }
  },
);

export const fetchMe = createAsyncThunk('auth/fetchMe', async (_, { rejectWithValue }) => {
  try {
    const res = await authApi.getMe();
    return res.data as AuthUser;
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } };
    return rejectWithValue(e.response?.data?.message || 'Failed to fetch user');
  }
});

export const logoutUser = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  // Runs while the session cookie is still valid. A thunk dispatch never throws,
  // so a push failure can't block logout.
  await dispatch(disablePushNotifications());
  await authApi.logout();
});

function clearSession(state: AuthState) {
  state.user = null;
  state.isAuthenticated = false;
  state.status = 'idle';
  state.error = null;
  state.pendingUserId = null;
  state.pendingAction = null;
  state.pendingMaskedPhone = null;
  state.pendingChannel = null;
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    setCredentials(state, action: PayloadAction<AuthTokens>) {
      state.user = action.payload.user;
      state.isAuthenticated = true;
    },
    forceLogout(state) {
      clearSession(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(registerUser.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.pendingUserId = payload.data.userId;
        state.pendingAction = 'REGISTER';
        state.pendingMaskedPhone = payload.data.maskedPhone;
        state.pendingChannel = payload.data.channel ?? 'sms';
      })
      .addCase(registerUser.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })

      .addCase(loginUser.pending, (state) => { state.status = 'loading'; state.error = null; state.accountStatus = null; state.suspensionReason = null; })
      .addCase(loginUser.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.accountStatus = null;
        state.suspensionReason = null;
        // Trusted device: backend skipped OTP and signed the session in directly
        if (payload.data.requiresOtp === false) {
          state.user = payload.data.user;
          state.isAuthenticated = true;
          state.pendingUserId = null;
          state.pendingAction = null;
          state.pendingMaskedPhone = null;
          state.pendingChannel = null;
        } else {
          state.pendingUserId = payload.data.userId;
          state.pendingAction = 'LOGIN';
          state.pendingMaskedPhone = payload.data.maskedPhone;
          state.pendingChannel = payload.data.channel ?? 'sms';
        }
      })
      .addCase(loginUser.rejected, (state, { payload }) => {
        state.status = 'failed';
        const p = payload as { message?: string; accountStatus?: string; suspensionReason?: string } | null;
        state.error = p?.message ?? 'Login failed';
        state.accountStatus = p?.accountStatus ?? null;
        state.suspensionReason = p?.suspensionReason ?? null;
      })

      .addCase(verifyOtp.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(verifyOtp.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.user = payload.user;
        state.isAuthenticated = true;
        state.pendingUserId = null;
        state.pendingAction = null;
        state.pendingMaskedPhone = null;
        state.pendingChannel = null;
      })
      .addCase(verifyOtp.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })

      .addCase(fetchMe.fulfilled, (state, { payload }) => {
        state.user = payload;
        state.isAuthenticated = true;
        state.initialized = true;
      })
      .addCase(fetchMe.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.initialized = true;
      })

      .addCase(logoutUser.fulfilled, clearSession)
      .addCase(logoutUser.rejected, clearSession);
  },
});

export const { clearError, setCredentials, forceLogout } = authSlice.actions;
export default authSlice.reducer;

export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthStatus = (state: { auth: AuthState }) => state.auth.status;
export const selectAuthInitialized = (state: { auth: AuthState }) => state.auth.initialized;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectAuthAccountStatus = (state: { auth: AuthState }) => state.auth.accountStatus;
export const selectAuthSuspensionReason = (state: { auth: AuthState }) => state.auth.suspensionReason;
export const selectPendingOtp = (state: { auth: AuthState }) => ({
  userId: state.auth.pendingUserId,
  action: state.auth.pendingAction,
  maskedPhone: state.auth.pendingMaskedPhone,
  channel: state.auth.pendingChannel,
});
